import React, { useState, useEffect } from "react";
import { FiBell, FiLock, FiCheckCircle, FiXCircle, FiClock } from "react-icons/fi";
import { db, auth } from "../../firebase";
import { collection, query, where, onSnapshot, doc, writeBatch, updateDoc } from "firebase/firestore";
import { notificationService } from "../../services/firestoreService";
import "./staffcss/medicalappointments.css";
import "../AdminPage.css";

const ProNotifications = ({ notifications: propNotifications, role = 'medical', onNotificationClick, onUnreadCountChange }) => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const unreadCount = React.useMemo(
    () => notifications.filter(n => n.readStatus === 'unread').length,
    [notifications]
  );
  const unreadBadgeLabel = unreadCount > 99 ? '99+' : unreadCount;

  const emitUnreadCount = React.useCallback((nextNotifications) => {
    if (typeof onUnreadCountChange === 'function') {
      onUnreadCountChange(nextNotifications.filter(n => n.readStatus === 'unread').length);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    if (Array.isArray(propNotifications)) {
      setNotifications(propNotifications);
      emitUnreadCount(propNotifications);
      setLoading(false);
      return undefined;
    }

    // Subscribe to persistent notifications under users/{uid}/notifications
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }
    // One-time diagnostic fetch to help debugging (falls back to realtime listener)
    (async () => {
      try {
        const existing = await notificationService.getFilteredNotifications(user.uid);
      } catch (err) {
        console.error('[ProNotifications] getFilteredNotifications error:', err);
      }
    })();

    const unsubscribe = notificationService.getUserNotificationsListener(user.uid, (notifDocs) => {
      const notifs = notifDocs.map(n => {
        const accessStatus = n.accessStatus || n.access_status || n.eventStatus || null;
        const statusLabel = accessStatus || (n.type === 'data_access_termination' ? 'EXPIRED' : '');
        const readStatus = n.status || 'unread'; // 'unread' or 'read'
        const createdAt = n.createdAt || new Date();
        const dateValue = createdAt instanceof Date ? createdAt : new Date(createdAt);

        return {
          id: n.id,
          type: n.type || 'data_access_request',
          status: statusLabel,
          readStatus: readStatus,
          title: n.notificationsTitle || (statusLabel === 'ACTIVE' ? 'Access Approved' : (statusLabel === 'DENIED' ? 'Access Denied' : (statusLabel === 'EXPIRED' ? 'Access Expired' : 'Access Update'))),
          desc: n.message || '',
          timestamp: dateValue,
          time: dateValue.toLocaleString(),
          icon: statusLabel === 'ACTIVE' ? <FiCheckCircle style={{ color: '#166534' }} /> : statusLabel === 'DENIED' ? <FiXCircle style={{ color: '#991b1b' }} /> : <FiClock style={{ color: '#d97706' }} />,
          patientUid: n.patientUid,
          appointmentId: n.appointmentId
        };
      });

      notifs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setNotifications(notifs);
      emitUnreadCount(notifs);
      setLoading(false);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const [readStatusFilter, setReadStatusFilter] = useState('all'); // 'all', 'unread', 'read'

  const categories = [
    { id: 'all', label: 'All Notifications' },
    { id: 'approved', label: 'Access Approved' },
    { id: 'revoked', label: 'Access Revoked' },
    { id: 'expired', label: 'Access Expired' },
    { id: 'denied', label: 'Access Denied' }
  ];

  const readStatusCategories = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread' },
    { id: 'read', label: 'Read' }
  ];

  // Calculate badge counts
  const badgeCounts = {
    all: notifications.length,
    unread: notifications.filter(n => n.readStatus === 'unread').length,
    read: notifications.filter(n => n.readStatus === 'read').length
  };

  // Filter notifications by read status first for category badge counts
  const notificationsByReadStatus = readStatusFilter === 'all'
    ? notifications
    : notifications.filter(n => n.readStatus === readStatusFilter);

  // Calculate category badge counts based on current read status filter
  const categoryBadgeCounts = {
    all: notificationsByReadStatus.length,
    approved: notificationsByReadStatus.filter(n => n.status === 'ACTIVE').length,
    revoked: notificationsByReadStatus.filter(n => n.status === 'REVOKED').length,
    expired: notificationsByReadStatus.filter(n => n.status === 'EXPIRED').length,
    denied: notificationsByReadStatus.filter(n => n.status === 'DENIED').length
  };

  const filteredNotifs = activeCategory === 'all'
    ? notifications
    : notifications.filter(n => {
        if (!n.status) return false;
        const st = n.status;
        switch (activeCategory) {
          case 'approved': return st === 'ACTIVE';
          case 'revoked': return st === 'REVOKED';
          case 'expired': return st === 'EXPIRED';
          case 'denied': return st === 'DENIED';
          default: return true;
        }
      });

  // Apply read status filter
  const finalFilteredNotifs = readStatusFilter === 'all'
    ? filteredNotifs
    : filteredNotifs.filter(n => n.readStatus === readStatusFilter);

  // Reset paging when filters or notifications change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, readStatusFilter, notifications.length]);

    const totalPages = Math.ceil(finalFilteredNotifs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedNotifs = finalFilteredNotifs.slice(startIndex, endIndex);

  const [selectedIds, setSelectedIds] = useState(new Set());

  const markAsRead = async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    
    try {
      const notifRef = doc(db, 'users', user.uid, 'notifications', id);
      await updateDoc(notifRef, { status: 'read' });
      
      // Update local state
      setNotifications(prev => {
        const next = prev.map(n => n.id === id ? { ...n, readStatus: 'read' } : n);
        emitUnreadCount(next);
        return next;
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    
    // Mark notification as read when selected
    markAsRead(id);
  };

  const handleNotificationClick = async (notif) => {
    // Mark notification as read
    await markAsRead(notif.id);

    // Call parent callback to switch to records panel and highlight
    if (onNotificationClick && notif.patientUid) {
      onNotificationClick(notif.patientUid, notif.appointmentId);
    }
  };

  const selectAll = () => {
    if (selectedIds.size === paginatedNotifs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedNotifs.map(n => n.id)));
      
      // Mark all visible notifications as read
      paginatedNotifs.forEach(n => markAsRead(n.id));
    }
  };

  const markAllAsRead = async () => {
    const user = auth.currentUser;
    if (!user) return;
    
    try {
      const batch = writeBatch(db);
      finalFilteredNotifs.forEach(n => {
        if (n.readStatus === 'unread') {
          const notifRef = doc(db, 'users', user.uid, 'notifications', n.id);
          batch.update(notifRef, { status: 'read' });
        }
      });
      await batch.commit();
      
      // Update local state
      setNotifications(prev => {
        const next = prev.map(n => n.readStatus === 'unread' ? { ...n, readStatus: 'read' } : n);
        emitUnreadCount(next);
        return next;
      });
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const user = auth.currentUser;
    if (!user) return;
    const batch = writeBatch(db);
    selectedIds.forEach(id => {
      const ref = doc(db, 'users', user.uid, 'notifications', id);
      batch.delete(ref);
    });
    try {
      await batch.commit();
      // Remove from local state
      setNotifications(prev => {
        const next = prev.filter(n => !selectedIds.has(n.id));
        emitUnreadCount(next);
        return next;
      });
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error deleting notifications:', err);
    }
  };

  return (
    <div className="account-management-container">
      {/* Header */}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Role Badge - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              {role === 'admin' ? 'Administrator' : 'Medical Professional'}
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{
              margin: 0,
              fontSize: '36px',
              fontWeight: '800',
              color: '#ffffff !important',
              background: 'none !important',
              WebkitBackgroundClip: 'unset !important',
              WebkitTextFillColor: '#ffffff !important',
              backgroundClip: 'unset !important',
              letterSpacing: '-0.02em',
              textShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span
                aria-label={`${unreadCount} unread notifications`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '28px',
                  height: '28px',
                  padding: '0 8px',
                  borderRadius: '999px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 800,
                  boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)',
                  lineHeight: 1,
                  border: '2px solid rgba(255,255,255,0.7)'
                }}
              >
                {unreadBadgeLabel}
              </span>
            )}
          </div>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Manage your notifications and alerts
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      <div className="ma-header-actions" style={{ marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={markAllAsRead}
            disabled={finalFilteredNotifs.filter(n => n.readStatus === 'unread').length === 0}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #3b82f6',
              backgroundColor: finalFilteredNotifs.filter(n => n.readStatus === 'unread').length === 0 ? '#f3f4f6' : '#dbeafe',
              color: finalFilteredNotifs.filter(n => n.readStatus === 'unread').length === 0 ? '#9ca3af' : '#1d4ed8',
              cursor: finalFilteredNotifs.filter(n => n.readStatus === 'unread').length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
          >
            Mark All as Read
          </button>
          <button
            onClick={selectAll}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #3b82f6',
              backgroundColor: '#dbeafe',
              color: '#1d4ed8',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            {selectedIds.size === paginatedNotifs.length && paginatedNotifs.length > 0 ? 'Deselect All' : 'Select All'}
          </button>
          <button
            onClick={deleteSelected}
            disabled={selectedIds.size === 0}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #fca5a5',
              backgroundColor: selectedIds.size === 0 ? '#fff' : '#fee2e2',
              color: '#991b1b',
              cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
          >
            Delete Selected
          </button>
        </div>

        {/* Read Status Tabs */}
        <div className="content-filter-tabs-wrapper">
          <div className="content-filter-tabs">
            {readStatusCategories.map(cat => (
              <button
                key={cat.id}
                className={`content-filter-tab ${readStatusFilter === cat.id ? 'active' : ''}`}
                onClick={() => setReadStatusFilter(cat.id)}
              >
                {cat.label}
                <span className="tab-count">
                  {badgeCounts[cat.id]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Tabs + Pagination Row */}
      <div className="content-filter-tabs-wrapper" style={{ marginBottom: '24px' }}>
        <div className="content-filter-tabs">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`content-filter-tab ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
              <span className="tab-count">
                {categoryBadgeCounts[cat.id]}
              </span>
            </button>
          ))}
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            {finalFilteredNotifs.length > 0 ? (
              `${startIndex + 1}-${Math.min(endIndex, finalFilteredNotifs.length)} of ${finalFilteredNotifs.length}`
            ) : (
              '0 of 0'
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '32px'
              }}
            >
              ←
            </button>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '32px'
              }}
            >
              →
            </button>
          </div>
        </div>
      </div>

      <div className="ma-notifications-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
          <>
            {[...Array(10)].map((_, i) => (
              <div 
                key={i}
                className="admin-aura-skeleton-row"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  gap: '16px'
                }}
              >
                <div style={{ width: '20px', height: '20px' }}><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100%', height: '100%', borderRadius: '4px' }} /></div>
                <div style={{ width: '44px', height: '44px' }}><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100%', height: '100%', borderRadius: '10px' }} /></div>
                <div style={{ flex: 1 }}>
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${40 + (i % 3) * 15}%`, height: '20px', marginBottom: '8px' }} />
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '25%', height: '14px', marginBottom: '12px' }} />
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 4) * 15}%`, height: '16px' }} />
                </div>
              </div>
            ))}
          </>
        ) : paginatedNotifs.length === 0 ? (
          <div className="ma-empty-state" style={{ 
            textAlign: 'center', 
            padding: '48px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '12px',
            color: '#6b7280'
          }}>
            <FiBell size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No notifications yet.</p>
          </div>
        ) : (
          paginatedNotifs.map(notif => (
            <div
              key={notif.id}
              className={`ma-notification-card ${notif.status} ${notif.readStatus === 'unread' ? 'unread' : 'read'}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '20px',
                backgroundColor: notif.readStatus === 'unread' ? '#f0f9ff' : '#fff',
                borderRadius: '12px',
                border: notif.readStatus === 'unread' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                gap: '16px',
                cursor: (role === 'medical' || role === 'admin') && notif.patientUid ? 'pointer' : 'default'
              }}
              onClick={() => (role === 'medical' || role === 'admin') && handleNotificationClick(notif)}
            >
              <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selectedIds.has(notif.id)} onChange={() => toggleSelect(notif.id)} style={{ marginRight: '12px' }} />
              </div>
              <div className="ma-notif-icon" style={{ 
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                borderRadius: '10px',
                backgroundColor: notif.status === 'ACTIVE' ? '#f0fdf4' : '#fef2f2'
              }}>
                {notif.icon}
              </div>
              <div className="ma-notif-content" style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: notif.readStatus === 'unread' ? 700 : 600, color: '#111827' }}>{notif.title}</h4>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{notif.time}</span>
                </div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: notif.readStatus === 'unread' ? 600 : 400, color: '#4b5563', lineHeight: 1.5, textAlign: 'left' }}>{notif.desc}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProNotifications;
